import type { UserPreferencesHydraItem, UserPreferencesPatch, UserPreferencesWrite } from "@verbleif/lib/src/composables/Api/Type/UserPreferences/user_preferences.interface";
import { createGlobalState } from "@vueuse/core";
import { useAuthStore } from "../../AuthStore/useAuthStore";
import { usePatchMutation, usePostMutation } from "../../Mutation/useHydraMutations";
import { fetchCollection } from "../../Query/resourceQuery";
import { refreshEffectiveUserPreferences } from "./refreshEffectiveUserPreferences";
import { pickOwnRow, unwrapPreferenceCollection } from "./userPreferenceScope.helpers";

export const useUserPreferences = createGlobalState(() => {
  const { userPreferences, user } = useAuthStore();
  const patchPreference = usePatchMutation<UserPreferencesHydraItem, UserPreferencesPatch>();
  const createPreference = usePostMutation<UserPreferencesHydraItem, UserPreferencesWrite>();

  async function refetchEffectiveMe(): Promise<UserPreferencesHydraItem | null> {
    return refreshEffectiveUserPreferences();
  }

  async function findUserWideRow(): Promise<UserPreferencesHydraItem | null> {
    if (user.value === null) {
      return null;
    }

    const data = await fetchCollection<UserPreferencesHydraItem>(`${user.value["@id"]}/user_preferences`);
    return pickOwnRow(unwrapPreferenceCollection(data), {
      scopeType: "user",
      userIri: user.value["@id"],
      locationIri: null,
    });
  }

  /** Requests run one after another, so a slow answer can never overwrite a newer choice. */
  let queue: Promise<void> = Promise.resolve();
  /** Calls that are queued or running. */
  let pending = 0;
  /** The last value the API accepted per key, what a failed change goes back to. */
  const confirmed: Record<string, unknown> = {};

  /**
   * Changes the signed-in user's own preferences for every location, optimistically: the app takes the new values at
   * once and the request runs in the background. On failure every key that still shows this call's value goes back
   * to the last value the API accepted.
   */
  function upsertUserPreference(input: UserPreferencesPatch): Promise<void> {
    if (user.value === null) {
      return Promise.resolve();
    }

    if (userPreferences.value !== null) {
      const current = userPreferences.value as unknown as Record<string, unknown>;
      for (const key of Object.keys(input)) {
        if (!(key in confirmed)) {
          confirmed[key] = current[key];
        }
      }
      Object.assign(userPreferences.value, input);
    }

    pending++;
    const run = queue.then(() => sendUpsert(input)).finally(() => {
      pending--;
    });
    queue = run.catch(() => undefined);
    return run;
  }

  async function sendUpsert(input: UserPreferencesPatch): Promise<void> {
    const owner = user.value;
    if (owner === null) {
      return;
    }
    try {
      const existing = await findUserWideRow();
      if (existing?.id) {
        await patchPreference.mutateAsync({
          url: `/api/user_preferences/${existing.id}`,
          body: input,
          config: { headers: { "Content-Type": "application/merge-patch+json" } },
        });
      } else {
        const payload: UserPreferencesWrite = {
          relatedUser: owner["@id"],
          ...input,
        };
        await createPreference.mutateAsync({
          url: "/api/user_preferences",
          body: payload,
        });
      }
      Object.assign(confirmed, input);
    } catch (error) {
      const current = userPreferences.value as unknown as Record<string, unknown> | null;
      if (current !== null) {
        for (const [key, value] of Object.entries(input)) {
          if (current[key] === value) {
            current[key] = confirmed[key];
          }
        }
      }
      try {
        await refetchWhenLast();
      } catch (refetchError) {
        console.error(refetchError);
      }
      throw error;
    }
    // The change is stored; a failed refetch must not report it as refused.
    try {
      await refetchWhenLast();
    } catch (refetchError) {
      console.error(refetchError);
    }
  }

  /**
   * Refetches the effective preferences after the last queued call only. A refetch while a later call still waits
   * would put the app back on the stored value of that call's keys, so a quick second toggle would flicker.
   */
  async function refetchWhenLast(): Promise<void> {
    if (pending > 1) {
      return;
    }
    await refetchEffectiveMe();
  }

  return {
    upsertUserPreference,
    userPreferences,
    refetchEffectiveMe,
  };
});
